import React, { createContext, useContext } from 'react';
import { resolveNestedZIndex } from '../constants/zIndex';

/**
 * Nesting-aware layering for overlays (PD-16154).
 *
 * Overlays portal to <body>, so their z-indexes all compete at the top of the
 * page no matter where in the component tree they were opened. A flat scale
 * cannot express "this select is inside that popover": the Select menu (1000)
 * opened inside a Popover (3000) rendered underneath it and could not be used.
 *
 * Each shared overlay therefore does two things:
 *   1. resolves its own z-index with `useLayerZIndex(ownValue)`, which lifts it
 *      above the overlay it is nested in (and leaves it alone otherwise), and
 *   2. wraps what it renders in `<LayerBoundary zIndex={resolved}>`, so the
 *      overlays opened inside it can do the same.
 *
 * React context follows the component tree, not the DOM, which is exactly the
 * relationship that matters here: a menu portalled to <body> is still a child
 * of the popover it was opened from.
 *
 * The context holds the z-index of the nearest enclosing overlay, or `null`
 * outside every overlay.
 */
export const LayerContext = createContext<number | null>(null);

/**
 * The z-index an overlay should render at: its own value at the top level, or
 * high enough to clear the overlay it is nested in. Never lower than `ownZIndex`.
 */
export function useLayerZIndex(ownZIndex: number): number {
	const parentZIndex = useContext(LayerContext);
	return resolveNestedZIndex(ownZIndex, parentZIndex);
}

interface LayerBoundaryProps {
	/** The z-index this overlay renders at, as resolved by `useLayerZIndex`. */
	zIndex: number;
	children?: React.ReactNode;
}

/**
 * Declares an overlay's z-index to the overlays rendered inside it. Wrap an
 * overlay's content in this whenever that content can open another overlay
 * (a select, a menu, a tooltip, a date picker).
 */
export function LayerBoundary({
	zIndex,
	children,
}: LayerBoundaryProps): React.ReactElement {
	return (
		<LayerContext.Provider value={zIndex}>{children}</LayerContext.Provider>
	);
}
